import React, { useRef } from 'react';

import './PlCapsuleChart.scss';
import 'widgets/fillsGraph/pl/PLChart.scss';
import { XAxisContextProvider } from 'utils/components/TimeSeriesChart/XAxisContext';
import { scaleLinear } from 'd3-scale';
import { styleConstants as tileStyleConstants } from 'widgets/tiles/components/styles/variables.module.constants';
import { TileTimeSeriesChart } from 'widgets/tiles/components/tile/TileTimeSeriesChart';
import { PlData } from 'widgets/plCapsule/plCapsuleData';
import { computeYDomain, getChartSeries } from 'widgets/fillsGraph/pl/series';
import {
    defaultFillsGraphConfiguration,
    FillsChartType,
} from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import { PlCapsuleChartHeaderValues } from 'widgets/plCapsule/components/chart/PlCapsuleChartHeaderValues';
import classNames from 'classnames';

export interface PlCapsuleChartProps {
    chartKey: string;
    hideRealized?: boolean;
    showUnrealized?: boolean;
    showTotal?: boolean;
    data: PlData;
    onClick: (time: number | undefined) => void;
}

export function PlCapsuleChart(props: PlCapsuleChartProps) {
    const { data, onClick, chartKey, hideRealized, showUnrealized, showTotal } =
        props;

    const { plSeries: timeSeries, startTime, endTime, breaksInTrading } = data;

    const showingItems =
        (hideRealized ? 0 : 1) +
            (showTotal ? 1 : 0) +
            (showUnrealized ? 1 : 0) >
        1;

    const height =
        tileStyleConstants.tileHeight -
        tileStyleConstants.tileWidgetToolbarHeight -
        tileStyleConstants.tileWidgetToolbarMarginTop -
        (showingItems ? 11 : 3);

    const series = getChartSeries(timeSeries, undefined);

    const yDomain = computeYDomain(
        {
            ...defaultFillsGraphConfiguration[FillsChartType.PnL],
            displayRealizedPnL: !hideRealized,
            displayUnrealizedPnL: !!showUnrealized,
            displayTotalPnL: !!showTotal,
        },
        timeSeries
    );
    const yScaleRef = useRef(scaleLinear());
    yScaleRef.current.domain(yDomain);

    const cssClasses = classNames(
        'PlCapsuleChart PLChart flippable',
        hideRealized && 'hide-realized',
        !showUnrealized && 'hide-unrealized',
        !showTotal && 'hide-total'
    );

    return (
        <div className={cssClasses}>
            <XAxisContextProvider
                timeSeriesChartWidth={tileStyleConstants.tileWidth}
                startTime={startTime}
                endTime={endTime}
                breaks={breaksInTrading}
                rangeLabels={[]}
                timeLineLabels={[]}
            >
                <PlCapsuleChartHeaderValues
                    data={data}
                    hideRealized={hideRealized}
                    showUnrealized={showUnrealized}
                    showTotal={showTotal}
                />
                <TileTimeSeriesChart
                    id={`PlCapsuleChart-${chartKey}`}
                    height={height}
                    data={timeSeries}
                    series={series}
                    displayXAxis={true}
                    yScale={yScaleRef.current}
                    yTicks={0}
                    onChartClick={onClick}
                />
            </XAxisContextProvider>
        </div>
    );
}
